import React, { Suspense, lazy } from "react"
import { Outlet } from "react-router-dom"
import { AppSidebar } from "@/components/layout/AppSidebar"
import { Menu } from "lucide-react"
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar"
import { useAuth } from "@/features/auth"
import { LoadingSpinner } from "@/components/ui/LoadingSpinner"
import { useRastreamentoUso } from "@/shared/hooks/useRastreamentoUso"

const AuthPage = lazy(() => import("@/features/auth/pages/AuthPage"))

/** Botão flutuante (só mobile) — único acesso ao menu lateral nessa largura */
function MobileMenuButton() {
  const { toggleSidebar } = useSidebar()

  return (
    <button
      type="button"
      onClick={toggleSidebar}
      aria-label="Abrir menu"
      className="md:hidden fixed top-3 left-3 z-40 h-10 w-10 flex items-center justify-center rounded-lg bg-[#0d0d0f] text-white shadow-lg ring-1 ring-white/10 active:scale-95 transition"
    >
      <Menu className="h-5 w-5" />
    </button>
  )
}

export default function DashboardLayout() {
  const { state: { isAuthenticated, isLoading } } = useAuth()
  useRastreamentoUso(isAuthenticated && !isLoading)

  // Sessão definitivamente inválida: mostra só a tela de login, sem sidebar.
  if (!isLoading && !isAuthenticated) {
    return (
      <Suspense fallback={<LoadingSpinner containerClassName="h-screen" size="lg" />}>
        <AuthPage />
      </Suspense>
    )
  }

  // Enquanto a sessão ainda está sendo verificada, o layout já monta (sidebar
  // em estado de esqueleto) para evitar o salto de "tela em branco" para
  // "sidebar aparecendo" quando a autenticação resolver.
  return (
    <SidebarProvider
      defaultOpen={false}
      style={{ '--sidebar-width': '72px' } as React.CSSProperties}
    >
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar loading={isLoading} />

        <div className="flex-1 flex flex-col min-w-0">
          <MobileMenuButton />

          <main className="flex-1 overflow-auto">
            {isLoading ? (
              <LoadingSpinner containerClassName="h-[80vh]" size="lg" />
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  )
}
